// Death / respawn panel, spectator label and scope overlay.

import { div, setHidden, setText, span } from "./dom";
import { weaponLabel } from "./format";
import type { DeathInfo } from "./types";

export class DeathPanel {
  readonly root = div("hud-death");
  private readonly kickerEl = div("hud-death__kicker");
  private readonly killerEl = div("hud-death__killer");
  private readonly weaponEl = div("hud-death__weapon");
  private readonly respawnEl = div("hud-death__respawn");
  private readonly respawnLabel = span("hud-death__rlabel");
  private readonly respawnNum = span("hud-death__rnum");
  private info: DeathInfo | null = null;
  private setAtMs = 0;
  private shownKey = Number.NaN;

  constructor() {
    this.respawnEl.append(this.respawnLabel, this.respawnNum);
    this.root.append(this.kickerEl, this.killerEl, this.weaponEl, this.respawnEl);
    this.root.hidden = true;
  }

  get isActive(): boolean {
    return this.info !== null;
  }

  set(info: DeathInfo | null, nowMs: number): void {
    this.info = info;
    this.root.hidden = info === null;
    if (!info) return;
    this.setAtMs = nowMs;
    const byPlayer = info.killer !== null;
    setText(this.kickerEl, byPlayer ? "KILLED BY" : "YOU DIED");
    this.killerEl.hidden = !byPlayer;
    if (byPlayer) setText(this.killerEl, info.killer!);
    const weapon = info.weapon === "" ? "" : weaponLabel(info.weapon);
    this.weaponEl.hidden = weapon === "";
    setText(this.weaponEl, weapon);
    this.update(nowMs);
  }

  /** Per frame: the respawn countdown. Numeric key per 0.1 s; the text is built only when it changes. */
  update(nowMs: number): void {
    const info = this.info;
    if (!info) return;
    let key: number; // -1 waiting for the next round, 0 respawning, else tenths of a second left
    if (info.respawnInSec === null) key = -1;
    else {
      const left = info.respawnInSec - (nowMs - this.setAtMs) / 1000;
      key = left > 0.05 ? Math.round(left * 10) : 0;
    }
    if (key === this.shownKey) return;
    this.shownKey = key;
    setText(this.respawnLabel, key < 0 ? "WAITING FOR NEXT ROUND" : key === 0 ? "RESPAWNING" : "RESPAWN IN");
    setHidden(this.respawnNum, key <= 0);
    if (key > 0) setText(this.respawnNum, (key / 10).toFixed(1));
  }
}

export class SpectatorLabel {
  readonly root = div("hud-spec");
  private readonly nameEl = span("hud-spec__name");
  private readonly hintEl = span("hud-spec__hint");
  private name: string | null = null;
  private hint: string | null = null;

  constructor() {
    this.hintEl.hidden = true;
    this.root.append(span("hud-spec__label", "SPECTATING"), this.nameEl, this.hintEl);
    this.root.hidden = true;
  }

  /** `hint`: what the player can do while watching ("FIRE: NEXT PLAYER"), null for nothing. */
  set(name: string | null, hint: string | null = null): void {
    if (name !== this.name) {
      this.name = name;
      this.root.hidden = name === null;
      if (name !== null) this.nameEl.textContent = name;
    }
    if (hint !== this.hint) {
      this.hint = hint;
      this.hintEl.hidden = hint === null;
      this.hintEl.textContent = hint ?? "";
    }
  }
}

export class ScopeOverlay {
  readonly root = div("hud-scope");
  private on = false;

  constructor() {
    this.root.append(div("hud-scope__h"), div("hud-scope__v"), div("hud-scope__dot"));
    this.root.hidden = true;
  }

  set(v: boolean): void {
    if (v === this.on) return;
    this.on = v;
    this.root.hidden = !v;
  }
}

/** Warmup of a custom lobby, panel closed: where the lobby controls are. */
export class LobbyHint {
  readonly root = div("hud-lobbyhint");
  private readonly keyEl = span("hud-lobbyhint__key");
  private readonly textEl = span("hud-lobbyhint__text");
  private key = "";
  private host: boolean | null = null;

  constructor() {
    this.root.append(this.keyEl, this.textEl);
    this.root.hidden = true;
  }

  /** `key` is the label of the lobby key ("L"); empty when it is unbound (the pause menu still opens the lobby). */
  set(show: boolean, host: boolean, key: string): void {
    this.root.hidden = !show;
    if (!show) return;
    if (key !== this.key) {
      this.key = key;
      this.keyEl.textContent = key;
      this.keyEl.hidden = key === "";
    }
    if (host !== this.host) {
      this.host = host;
      this.textEl.textContent = host ? "LOBBY \u00b7 START THE MATCH WHEN READY" : "LOBBY \u00b7 WAITING FOR THE HOST";
    }
  }
}
